import {inject, injectable} from 'inversify'
import {CreateOrderRequest, OrderServiceInterface, OrderTotalsRequest} from '@/src/ports/services'
import type {IErrorReporter} from '@/src/ports/services/monitoring'
import {IErrorReporterSymbol} from '@/src/ports/services/monitoring'
import {OrderTotals} from '@/src/entities/order'
import {OrderError} from '@/src/core/domain/errors'
import {OperationResult, Result} from '@/src/shared/utils/result'
import {Routes} from '@/src/config/routes'
import type {IApiClientServiceFactory} from '@/src/ports/services/api'
import {IApiClientServiceFactorySymbol} from '@/src/ports/services/api'

/**
 * Order Service Implementation
 *
 * Handles order-related API calls.
 * Returns Result type for explicit error handling.
 */
@injectable()
export class OrderService implements OrderServiceInterface {
  constructor(
    @inject(IErrorReporterSymbol) private errorReporter: IErrorReporter,
    @inject(IApiClientServiceFactorySymbol) private apiClientFactory: IApiClientServiceFactory,
  ) {}

  async create(data: CreateOrderRequest): Promise<OperationResult<string, OrderError>> {
    try {
      const client = this.apiClientFactory()
      const orderId = await client.post<string>(Routes.webApi.order.create, {data})
      return Result.ok(orderId)
    } catch (e: unknown) {
      this.errorReporter.capture(
        e instanceof Error ? e : new Error(String(e)),
        {location_id: data.location_id, delivery_service_id: data.delivery_service_id},
      )
      return Result.fail(new OrderError('CREATE_FAILED', e))
    }
  }

  async getTotals(data: OrderTotalsRequest): Promise<OperationResult<OrderTotals, OrderError>> {
    try {
      const client = this.apiClientFactory()
      const totals = await client.post<OrderTotals>(Routes.webApi.order.totals, {
        data: {
          person_type: data.person_type,
          location_id: data.location_id,
          delivery_service_id: data.delivery_service_id ?? null,
          payment_service_id: data.payment_service_id ?? null,
          pickup_point_id: data.pickup_point_id ?? null,
          bonus_amount: data.bonus_amount ?? 0,
          promo_code: data.promo_code ?? null,
          properties: data.properties ?? [],
        },
      })
      return Result.ok(totals)
    } catch (e: unknown) {
      this.errorReporter.capture(
        e instanceof Error ? e : new Error(String(e)),
        {location_id: data.location_id, delivery_service_id: data.delivery_service_id},
      )
      return Result.fail(new OrderError('CALCULATE_TOTALS_FAILED', e))
    }
  }
}
